import React from "react";
import { Typography, Space, Badge } from "antd";
import { ColumnsType } from "antd/es/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTheme } from "../../../context/ThemeContext";
import Table from "../../../commonComponents/Table";
import { TrendingItem, TrendingItemsTableProps } from "./types";
import {
  RankText,
  RevenueText,
  SmallText,
  SmallProgress,
  VolumeGrowthText,
  RevenueGrowthText,
  IconWrapper,
} from "./styles";
import {
  getTrendBadgeColor,
  trendLabels,
  trendIconConfig,
  TrendType,
} from "./constants";

const { Text } = Typography;

const TrendingItemsTable: React.FC<TrendingItemsTableProps> = ({
  items,
  isLoading,
}) => {
  const { isDarkMode } = useTheme();
  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";

  const getTrendIcon = (trend: TrendType): React.ReactElement => {
    const config = trendIconConfig[trend];
    return (
      <IconWrapper color={config.color}>
        <FontAwesomeIcon icon={config.icon} />
      </IconWrapper>
    );
  };

  const columns: ColumnsType<TrendingItem> = [
    {
      title: "Rank",
      key: "rank",
      render: (_: unknown, __: unknown, index: number) => (
        <RankText strong>#{index + 1}</RankText>
      ),
    },
    {
      title: "Item",
      dataIndex: "item",
      key: "item",
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      render: (text: string) => <Badge color="blue" text={text} />,
    },
    {
      title: "Times Sold",
      dataIndex: "totalTimesSold",
      key: "totalTimesSold",
      render: (value: number) => <Text strong>{value.toLocaleString()}</Text>,
    },
    {
      title: "Revenue",
      dataIndex: "totalRevenue",
      key: "totalRevenue",
      render: (value: number) => (
        <RevenueText strong>${value.toLocaleString()}</RevenueText>
      ),
    },
    {
      title: "Popularity",
      dataIndex: "popularityScore",
      key: "popularityScore",
      render: (score: number) => (
        <div>
          <SmallText strong>{score}/100</SmallText>
          <SmallProgress percent={score} strokeColor="#1890ff" size="small" />
        </div>
      ),
    },
    {
      title: "Growth",
      key: "growth",
      render: (_: unknown, record: TrendingItem) => (
        <div>
          <VolumeGrowthText>
            Vol: {record.volumeGrowth > 0 ? "+" : ""}
            {record.volumeGrowth}%
          </VolumeGrowthText>
          <RevenueGrowthText>
            Rev: {record.revenueGrowth > 0 ? "+" : ""}
            {record.revenueGrowth}%
          </RevenueGrowthText>
        </div>
      ),
    },
    {
      title: "Trend",
      dataIndex: "trend",
      key: "trend",
      render: (trend: TrendType) => (
        <Space>
          {getTrendIcon(trend)}
          <Badge
            status={getTrendBadgeColor(trend)}
            text={trendLabels[trend]}
          />
        </Space>
      ),
    },
  ];

  return (
    <Table<TrendingItem>
      columns={columns}
      dataSource={items}
      rowKey={(record: TrendingItem, index?: number) =>
        `${record.item}-${index || 0}`
      }
      loading={isLoading}
      pagination={false}
      scroll={{ x: true, y: 600 }}
      emptyText="No trending items found"
      emptyDescription="No trending items found"
      headerBackgroundColor={isDarkMode ? "#1a202c" : "#F2F2F2"}
      removeVerticalBorders={true}
      thPadding="15px 10px"
      tdPadding="10px 10px"
      firstChildBorderRadius="8px"
      border="0"
      borderBottomLeftRight={true}
      theme={themeMode}
    />
  );
};

export default TrendingItemsTable;
